/**
 * API Cache Hook
 * Provides in-memory caching for API requests to reduce duplicate calls
 *
 * @package
 */

import { useState, useEffect, useRef } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';

/**
 * In-memory cache storage
 * Shared across all hook instances
 */
const cache = new Map();

/**
 * Cache statistics for debugging
 */
const cacheStats = {
	hits: 0,
	misses: 0,
	size: () => cache.size,
};

/**
 * Get cache statistics
 *
 * @return {Object} Cache statistics
 */
export const getCacheStats = () => ( {
	hits: cacheStats.hits,
	misses: cacheStats.misses,
	size: cacheStats.size(),
	hitRate: cacheStats.hits + cacheStats.misses > 0
		? ( cacheStats.hits / ( cacheStats.hits + cacheStats.misses ) * 100 ).toFixed( 2 ) + '%'
		: '0%',
} );

/**
 * Clear the entire cache
 */
export const clearCache = () => {
	cache.clear();
	cacheStats.hits = 0;
	cacheStats.misses = 0;
};

/**
 * Remove a specific cache entry
 *
 * @param {string} key Cache key to remove
 */
export const removeCacheEntry = key => {
	cache.delete( key );
};

/**
 * Read from cache synchronously (for initial state so we avoid loading flash on re-mount).
 *
 * @param {string} cacheKey Cache key (API path).
 * @param {number} ttl      Time to live in ms.
 *
 * @return {Object|null} Cached entry { data } or null.
 */
function getCached( cacheKey, ttl ) {
	const cached = cache.get( cacheKey );
	if ( cached && Date.now() - cached.timestamp < ttl ) {
		return cached;
	}
	return null;
}

/**
 * In-flight requests keyed by path, so concurrent consumers of the same
 * resource (e.g. the same product in a card, the summary and the total)
 * share one request instead of racing duplicates.
 */
const inflight = new Map();

const storeInCache = ( path, data ) => {
	cache.set( path, {
		data,
		timestamp: Date.now(),
	} );
};

/**
 * The vehicle currently on screen, or 0 before the app has mounted.
 */
let vehicleConfigId = 0;

/**
 * Record which vehicle the configurator is showing.
 *
 * Fitting hours belong to a product/vehicle pair rather than to a product, so product
 * requests have to name the vehicle and it forms part of the cache key.
 *
 * @param {number|string} id at_vehicle_config post ID.
 */
export const setVehicleConfigId = id => {
	const parsed = parseInt( id, 10 );
	vehicleConfigId = parsed > 0 ? parsed : 0;
};

/**
 * Add the vehicle config to a product request so the response carries fitting hours.
 *
 * @param {string} path API path.
 * @return {string} Path with the vehicle config appended where it applies.
 */
const withVehicleConfig = path => {
	if ( ! vehicleConfigId || ! path.includes( '/qcom_product' ) ) {
		return path;
	}

	return `${ path }${ path.includes( '?' ) ? '&' : '?' }vehicle_config=${ vehicleConfigId }`;
};

/**
 * Fetch a path through the shared cache, deduplicating in-flight requests.
 *
 * Plain async alternative to the useApiCache hook for non-hook call sites.
 *
 * @param {string} path API path to fetch.
 * @param {number} ttl  Time to live in milliseconds (default: 5 minutes).
 *
 * @return {Promise<*>} Resolves with the response data.
 */
export const cachedApiFetch = ( path, ttl = 300000 ) => {
	// The vehicle is part of the key, not just the request, so a response fetched for one
	// vehicle can never be handed to another.
	const key = withVehicleConfig( path );

	const cached = getCached( key, ttl );
	if ( cached ) {
		cacheStats.hits++;
		return Promise.resolve( cached.data );
	}

	if ( inflight.has( key ) ) {
		cacheStats.hits++;
		return inflight.get( key );
	}

	cacheStats.misses++;
	const promise = apiFetch( { path: key } )
		.then( result => {
			storeInCache( key, result );
			inflight.delete( key );
			return result;
		} )
		.catch( error => {
			inflight.delete( key );
			throw error;
		} );

	inflight.set( key, promise );
	return promise;
};

const productEmbedPath = id => withVehicleConfig( `/wp/v2/qcom_product/${ id }?_embed` );
const productPlainPath = id => withVehicleConfig( `/wp/v2/qcom_product/${ id }` );

/**
 * Prefetch products via the batch endpoint and seed the per-product cache keys.
 *
 * One request per chunk replaces one request per product card. Both the `?_embed`
 * and plain paths are seeded so cards, modals and the total-cost hook all hit cache.
 * Unpublished products are omitted by the endpoint; their per-product promises
 * reject so consumers show an error state instead of firing a 401 fallback request.
 *
 * @param {Array<number|string>} productIds Product IDs to prefetch.
 */
export const prefetchProducts = productIds => {
	const CHUNK_SIZE = 25;
	const ids = [ ...new Set(
		( productIds || [] )
			.map( id => parseInt( id, 10 ) )
			.filter( id => id > 0 )
	) ].filter( id => ! cache.has( productEmbedPath( id ) ) && ! inflight.has( productEmbedPath( id ) ) );

	for ( let i = 0; i < ids.length; i += CHUNK_SIZE ) {
		const chunk = ids.slice( i, i + CHUNK_SIZE );
		const vehicleParam = vehicleConfigId ? `&vehicle_config=${ vehicleConfigId }` : '';
		const batchPromise = apiFetch( {
			path: `/at-configurator/v1/products?ids=${ chunk.join( ',' ) }${ vehicleParam }`,
		} );

		chunk.forEach( id => {
			const embedPath = productEmbedPath( id );
			const plainPath = productPlainPath( id );

			const promise = batchPromise
				.then( map => {
					const product = map?.[ String( id ) ];
					if ( ! product ) {
						throw new Error( `Product ${ id } is not available` );
					}
					storeInCache( embedPath, product );
					storeInCache( plainPath, product );
					return product;
				} )
				// If the batch request itself fails, fall back to an individual fetch.
				.catch( error => {
					if ( error?.message === `Product ${ id } is not available` ) {
						throw error;
					}
					return apiFetch( { path: embedPath } ).then( product => {
						storeInCache( embedPath, product );
						storeInCache( plainPath, product );
						return product;
					} );
				} )
				.finally( () => {
					inflight.delete( embedPath );
					inflight.delete( plainPath );
				} );

			// Detached no-op handler: silences unhandled-rejection noise for products
			// nobody ends up rendering, without swallowing the rejection for consumers.
			promise.catch( () => {} );

			inflight.set( embedPath, promise );
			inflight.set( plainPath, promise );
		} );
	}
};

/**
 * Custom hook for cached API fetches
 *
 * @param {string}  path            API path to fetch
 * @param {Object}  options         Additional options
 * @param {number}  options.ttl     Time to live in milliseconds (default: 5 minutes)
 * @param {boolean} options.enabled Whether to fetch (default: true)
 * @return {Object} Object containing data, loading state, error, and refetch function
 */
export const useApiCache = ( path, options = {} ) => {
	const {
		ttl = 300000,
		enabled = true,
	} = options;

	// Must match the key cachedApiFetch() stores under, or the synchronous read below
	// misses and the component flashes a loading state it already has data for.
	const cacheKey = path ? withVehicleConfig( path ) : path;
	const initialCached = enabled && path ? getCached( cacheKey, ttl ) : null;

	const [ data, setData ] = useState( () => ( initialCached ? initialCached.data : null ) );
	const [ loading, setLoading ] = useState( () => ! ( enabled && path && initialCached ) );
	const [ error, setError ] = useState( null );
	const isMounted = useRef( true );

	useEffect( () => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, [] );

	const fetchData = async () => {
		if ( ! enabled || ! path ) {
			setLoading( false );
			return;
		}

		const cached = getCached( cacheKey, ttl );
		if ( cached ) {
			cacheStats.hits++;
			if ( isMounted.current ) {
				setData( cached.data );
				setLoading( false );
				setError( null );
			}
			return;
		}

		setLoading( true );

		try {
			const result = await cachedApiFetch( path, ttl );

			if ( isMounted.current ) {
				setData( result );
				setError( null );
			}
		} catch ( err ) {
			if ( isMounted.current ) {
				setError( err );
			}
		} finally {
			if ( isMounted.current ) {
				setLoading( false );
			}
		}
	};

	useEffect( () => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ path, enabled ] );

	const refetch = () => {
		removeCacheEntry( cacheKey );
		fetchData();
	};

	return {
		data,
		loading,
		error,
		refetch,
	};
};

/**
 * Hook for fetching product data with caching
 *
 * @param {number} productId Product ID to fetch
 * @param {Object} options   Additional options
 * @return {Object} Object containing product data, loading state, error, and refetch function
 */
export const useCachedProduct = ( productId, options = {} ) => {
	const path = productId ? `/wp/v2/qcom_product/${ productId }?_embed` : null;
	return useApiCache( path, { ...options, enabled: !! productId } );
};

/**
 * Hook for fetching bundle data with caching
 *
 * @param {number} bundleId Bundle ID to fetch
 * @param {Object} options  Additional options
 * @return {Object} Object containing bundle data, loading state, error, and refetch function
 */
export const useCachedBundle = ( bundleId, options = {} ) => {
	const path = bundleId ? `/wp/v2/bundles/${ bundleId }?_embed` : null;
	return useApiCache( path, { ...options, enabled: !! bundleId } );
};
